// HyperlinkEditor.tsx
import React from 'react';
import cx from 'classnames';

import './HyperlinkEditor.styles.scss';

export interface HyperlinkEditorProps {
  value: string;
  onChange: (value: string) => void;
  onUpload: () => void;
  autoFocus?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onSubmit?: (value: string) => void;
  onBlur?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  onClick?: () => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const HyperlinkEditor: React.FC<HyperlinkEditorProps> = ({
  value,
  onChange,
  onUpload,
  autoFocus = false,
  className = "",
  style = {},
  onSubmit,
  onBlur,
  onKeyDown,
  disabled = false,
  onClick,
  onMouseLeave,
  onMouseEnter,
  inputRef,
}) => {
  const inputStyle = React.useMemo<React.CSSProperties>(() => {
    const { color, fontFamily, fontSize, textAlign, fontStyle, fontWeight, textDecoration } = style;
    return {
      color,
      fontFamily,
      fontSize,
      textAlign,
      fontStyle,
      fontWeight,
      textDecoration,
    };
  }, [style]);

  return (
    <div 
      className={cx('HyperlinkEditor', className, { 'HyperlinkEditor--disabled': disabled })}
      style={style}
      onMouseDown={(e) => {
        // prevent cell selection on click
        e.stopPropagation();
                  onClick?.();

      }}
      onMouseLeave={(e) => {
        onMouseLeave?.(e);
      }}
      onMouseEnter={(e) => {
        onMouseEnter?.(e);
      }}
    >
      {/* Left: hyperlink icon */}
      <img
        src="gif/hyperlink.gif"
        alt="Link"
        className="HyperlinkEditor__icon"
        onMouseDown={(e) => e.preventDefault()}
      />
      
      {/* Value input area */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        onChange={(e) => onChange(e.target.value)}
        onPaste={(e) => {
          // Keep native paste behavior while preventing table-level handlers from interfering.
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onSubmit?.(value);
          }
          // prevent spreadsheet global handlers from stealing focus/keys
          if (e.key !== 'Tab') {
            e.stopPropagation();
          }
        }}
        onBlur={(e) => {
          if (onBlur) {
            onBlur(e.target.value);
          }
        }}
        className="HyperlinkEditor__input"
        style={inputStyle}
        onMouseDown={(e) =>
           e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        readOnly={disabled}
      />

      {/* Right: upload icon button */}
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) {
            onUpload();
          }
        }}
        title="Upload Document"
        className="HyperlinkEditor__upload-button"
        disabled={disabled}
      >
        <img src="gif/upload.gif" alt="Upload" className="HyperlinkEditor__upload-icon" />
      </button>
    </div>
  );
};